import React, { createContext, useContext, useState, useCallback } from 'react';

const ImageModalContext = createContext();

export const useImageModal = () => {
  const context = useContext(ImageModalContext);
  if (!context) {
    throw new Error('useImageModal must be used within an ImageModalProvider');
  }
  return context;
};

export const ImageModalProvider = ({ children }) => {
  const [modalState, setModalState] = useState({
    isOpen: false,
    images: [],
    currentIndex: 0,
    sectionId: null,
    transitioning: false,
    direction: 'none'
  });

  const openModal = useCallback((images, currentIndex, sectionId) => {
    setModalState(prevState => {
      // If a modal is already open from a different section, close it with transition
      if (prevState.isOpen && prevState.sectionId !== sectionId) {
        return {
          ...prevState,
          transitioning: true,
          direction: 'closing'
        };
      }
      
      return {
        isOpen: true,
        images,
        currentIndex,
        sectionId,
        transitioning: false,
        direction: 'opening'
      };
    });

    // If transitioning from another section, delay opening new modal
    if (modalState.isOpen && modalState.sectionId !== sectionId) {
      setTimeout(() => {
        setModalState({
          isOpen: true,
          images,
          currentIndex,
          sectionId,
          transitioning: false,
          direction: 'opening'
        });
      }, 300); // Match the closing animation duration
    }
  }, [modalState.isOpen, modalState.sectionId]);

  const closeModal = useCallback(() => {
    setModalState(prevState => ({
      ...prevState,
      transitioning: true,
      direction: 'closing'
    }));

    setTimeout(() => {
      setModalState({
        isOpen: false,
        images: [],
        currentIndex: 0,
        sectionId: null,
        transitioning: false,
        direction: 'none'
      });
    }, 300);
  }, []);

  const goToNext = useCallback(() => {
    setModalState(prevState => ({
      ...prevState,
      currentIndex: prevState.currentIndex === prevState.images.length - 1 
        ? 0 
        : prevState.currentIndex + 1,
      direction: 'next'
    }));
  }, []);

  const goToPrev = useCallback(() => {
    setModalState(prevState => ({
      ...prevState,
      currentIndex: prevState.currentIndex === 0 
        ? prevState.images.length - 1 
        : prevState.currentIndex - 1,
      direction: 'prev'
    }));
  }, []);

  const setCurrentIndex = useCallback((index) => {
    setModalState(prevState => ({
      ...prevState,
      currentIndex: index,
      direction: 'jump'
    }));
  }, []);

  return (
    <ImageModalContext.Provider value={{
      modalState,
      openModal,
      closeModal,
      goToNext,
      goToPrev,
      setCurrentIndex
    }}>
      {children}
    </ImageModalContext.Provider>
  );
};
